// src/services/sharePointService.ts
// SharePoint Lists integration via Netlify Function proxy
// No authentication required - handled server-side

// ═══════════════════════════════════════════════════════════════════════════
// Configuration
// ═══════════════════════════════════════════════════════════════════════════

const PROXY_URL = '/.netlify/functions/sharepoint-proxy';

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface Schedule {
  id: string;
  title: string;
  reportType: 'LoadSheet' | 'OrderReport' | 'DriverSheet';
  isActive: boolean;
  runTime: string;
  daysOfWeek: string[];
  recipientListName: string;
  driverFilter: string;
  parameters: Record<string, string>;
  lastRun?: string;
  lastRunStatus?: 'success' | 'failed' | 'running';
}

export interface Recipient {
  id: string;
  name: string;
  email: string;
  groups: string[];           // LoadTeam | SalesTeam | Management
  isActive: boolean;
}

export interface Driver {
  id: string;
  driverCode: string;
  driverName: string;
  email: string;              // One or more emails, comma-separated
  isActive: boolean;
}

export interface RunHistoryItem {
  id: string;
  title: string;              // e.g. LoadSheet-S1-2026-03-09
  runDate: string;
  reportType: string;
  sendNumber: 1 | 2;
  status: 'Success' | 'Failed' | 'Skipped';
  recipientCount: number;
  skipReason?: string;
  errorMessage?: string;
  runAt: string;
}

export interface PowerBIEmbedConfig {
  reportId: string;
  embedUrl: string;
  embedToken: string;
  expiration: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// API Helper
// ═══════════════════════════════════════════════════════════════════════════

interface ProxyResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

async function proxyRequest<T>(
  action: string,
  params: Record<string, unknown> = {}
): Promise<T> {
  const response = await fetch(PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...params }),
  });

  const result: ProxyResponse<T> = await response.json();

  if (!result.success) {
    throw new Error(result.error || 'Request failed');
  }

  return result.data as T;
}

// ═══════════════════════════════════════════════════════════════════════════
// SharePoint List Item Mapping
// ═══════════════════════════════════════════════════════════════════════════

interface SharePointItem {
  id: string;
  fields: Record<string, unknown>;
}

function mapSchedule(item: SharePointItem): Schedule {
  const f = item.fields;
  return {
    id: item.id,
    title: (f.Title as string) || '',
    reportType: (f.ReportType as Schedule['reportType']) || 'LoadSheet',
    isActive: (f.IsActive as boolean) || false,
    runTime: (f.RunTime as string) || '05:00',
    daysOfWeek: parseJsonField(f.DaysOfWeek, []),
    recipientListName: (f.RecipientListName as string) || '',
    driverFilter: (f.DriverFilter as string) || 'ALL',
    parameters: parseJsonField(f.Parameters, {}),
    lastRun: f.LastRun as string | undefined,
    lastRunStatus: f.LastRunStatus as Schedule['lastRunStatus'],
  };
}

function mapRecipient(item: SharePointItem): Recipient {
  const f = item.fields;
  // SharePoint multi-choice returns as semicolon string or array depending on context
  const rawGroups = f.DriverFilter;
  const groups: string[] = Array.isArray(rawGroups)
    ? (rawGroups as string[]).filter(Boolean)
    : typeof rawGroups === 'string' && rawGroups.trim()
    ? rawGroups.split(';').map(s => s.trim()).filter(Boolean)
    : [];
  return {
    id: item.id,
    name: (f.Title as string) || '',
    email: (f.Email1 as string) || '',
    groups,
    isActive: (f.IsActive as boolean) ?? true,
  };
}

function mapDriver(item: SharePointItem): Driver {
  const f = item.fields;
  return {
    id: item.id,
    driverName: (f.Title as string) || '',
    driverCode: (f.DriverCode as string) || '',
    email: (f.Email1 as string) || '',
    isActive: (f.IsActive as boolean) ?? true,
  };
}

function mapRunHistory(item: SharePointItem): RunHistoryItem {
  const f = item.fields;
  return {
    id: item.id,
    title: (f.Title as string) || '',
    runDate: (f.RunDate as string) || '',
    reportType: (f.ReportType as string) || '',
    sendNumber: (Number(f.SendNumber) === 2 ? 2 : 1),
    status: (f.Status as RunHistoryItem['status']) || 'Failed',
    recipientCount: (f.RecipientCount as number) || 0,
    skipReason: f.SkipReason as string | undefined,
    errorMessage: f.ErrorMessage as string | undefined,
    runAt: (f.RunAt as string) || '',
  };
}

function parseJsonField<T>(value: unknown, defaultValue: T): T {
  if (!value) return defaultValue;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return defaultValue;
    }
  }
  return value as T;
}

// ═══════════════════════════════════════════════════════════════════════════
// Schedule Operations
// ═══════════════════════════════════════════════════════════════════════════

export async function getSchedules(): Promise<Schedule[]> {
  const items = await proxyRequest<SharePointItem[]>('getSchedules');
  return items.map(mapSchedule);
}

export async function createSchedule(schedule: Omit<Schedule, 'id'>): Promise<Schedule> {
  const fields = {
    Title: schedule.title,
    ReportType: schedule.reportType,
    IsActive: schedule.isActive,
    RunTime: schedule.runTime,
    DaysOfWeek: JSON.stringify(schedule.daysOfWeek),
    RecipientListName: schedule.recipientListName,
    DriverFilter: schedule.driverFilter,
    Parameters: JSON.stringify(schedule.parameters),
  };

  const result = await proxyRequest<SharePointItem>('createItem', {
    listName: 'ReportSchedules',
    body: fields,
  });

  return mapSchedule(result);
}

export async function updateSchedule(id: string, updates: Partial<Schedule>): Promise<void> {
  const fields: Record<string, unknown> = {};

  if (updates.title !== undefined) fields.Title = updates.title;
  if (updates.reportType !== undefined) fields.ReportType = updates.reportType;
  if (updates.isActive !== undefined) fields.IsActive = updates.isActive;
  if (updates.runTime !== undefined) fields.RunTime = updates.runTime;
  if (updates.daysOfWeek !== undefined) fields.DaysOfWeek = JSON.stringify(updates.daysOfWeek);
  if (updates.recipientListName !== undefined) fields.RecipientListName = updates.recipientListName;
  if (updates.driverFilter !== undefined) fields.DriverFilter = updates.driverFilter;
  if (updates.parameters !== undefined) fields.Parameters = JSON.stringify(updates.parameters);
  if (updates.lastRun !== undefined) fields.LastRun = updates.lastRun;
  if (updates.lastRunStatus !== undefined) fields.LastRunStatus = updates.lastRunStatus;

  await proxyRequest('updateItem', {
    listName: 'ReportSchedules',
    itemId: id,
    body: fields,
  });
}

export async function deleteSchedule(id: string): Promise<void> {
  await proxyRequest('deleteItem', {
    listName: 'ReportSchedules',
    itemId: id,
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Recipient Operations
// ═══════════════════════════════════════════════════════════════════════════

export async function getRecipients(): Promise<Recipient[]> {
  const items = await proxyRequest<SharePointItem[]>('getRecipients');
  return items.map(mapRecipient);
}

export async function createRecipient(recipient: Omit<Recipient, 'id'>): Promise<Recipient> {
  // SharePoint multi-choice columns expect semicolon-delimited string via Graph API
  const fields: Record<string, unknown> = {
    Title: recipient.name,
    Email: recipient.email,
    IsActive: recipient.isActive,
    ...(recipient.groups?.length ? { DriverFilter: recipient.groups.join(';') } : {}),
  };

  const result = await proxyRequest<SharePointItem>('createItem', {
    listName: 'ReportRecipients',
    body: fields,
  });

  return mapRecipient(result);
}

export async function updateRecipient(id: string, updates: Partial<Recipient>): Promise<void> {
  const fields: Record<string, unknown> = {};

  if (updates.name !== undefined) fields.Title = updates.name;
  if (updates.email !== undefined) fields.Email = updates.email;
  if (updates.groups !== undefined) fields.DriverFilter = updates.groups.length > 0 ? updates.groups.join(';') : '';
  if (updates.isActive !== undefined) fields.IsActive = updates.isActive;

  await proxyRequest('updateItem', {
    listName: 'ReportRecipients',
    itemId: id,
    body: fields,
  });
}

export async function deleteRecipient(id: string): Promise<void> {
  await proxyRequest('deleteItem', {
    listName: 'ReportRecipients',
    itemId: id,
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Driver Operations
// ═══════════════════════════════════════════════════════════════════════════

export async function getDrivers(): Promise<Driver[]> {
  const items = await proxyRequest<SharePointItem[]>('getDrivers');
  return items.map(mapDriver);
}

export async function createDriver(driver: Omit<Driver, 'id'>): Promise<Driver> {
  const fields = {
    Title: driver.driverName,
    DriverCode: driver.driverCode,
    Email1: driver.email,
    IsActive: driver.isActive,
  };
  const result = await proxyRequest<SharePointItem>('createItem', {
    listName: 'Drivers',
    body: fields,
  });
  return mapDriver(result);
}

export async function updateDriver(id: string, updates: Partial<Driver>): Promise<void> {
  const fields: Record<string, unknown> = {};

  if (updates.driverName !== undefined) fields.Title = updates.driverName;
  if (updates.driverCode !== undefined) fields.DriverCode = updates.driverCode;
  if (updates.email !== undefined) fields.Email1 = updates.email;
  if (updates.isActive !== undefined) fields.IsActive = updates.isActive;

  await proxyRequest('updateItem', {
    listName: 'Drivers',
    itemId: id,
    body: fields,
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Run History Operations
// ═══════════════════════════════════════════════════════════════════════════

export async function getRunHistory(): Promise<RunHistoryItem[]> {
  const items = await proxyRequest<SharePointItem[]>('getHistory');
  return items.map(mapRunHistory).sort((a, b) => new Date(b.runAt).getTime() - new Date(a.runAt).getTime());
}

export async function createRunHistoryEntry(entry: Omit<RunHistoryItem, 'id'>): Promise<RunHistoryItem> {
  const fields = {
    Title: entry.title,
    RunDate: entry.runDate,
    ReportType: entry.reportType,
    SendNumber: entry.sendNumber,
    Status: entry.status,
    RecipientCount: entry.recipientCount,
    SkipReason: entry.skipReason || '',
    ErrorMessage: entry.errorMessage || '',
    RunAt: entry.runAt,
  };

  const result = await proxyRequest<SharePointItem>('createItem', {
    listName: 'RunHistory',
    body: fields,
  });

  return mapRunHistory(result);
}

// ═══════════════════════════════════════════════════════════════════════════
// Power BI Operations
// ═══════════════════════════════════════════════════════════════════════════

export async function getReportEmbedConfig(reportId: string): Promise<PowerBIEmbedConfig> {
  return proxyRequest<PowerBIEmbedConfig>('getEmbedToken', { reportId });
}

export async function listPowerBIReports(): Promise<{ id: string; name: string }[]> {
  const result = await proxyRequest<{ value: { id: string; name: string }[] }>('listReports');
  return result.value || [];
}

export async function exportReportToPdf(
  reportId: string,
  parameters?: Record<string, string>
): Promise<string> {
  const { exportId } = await proxyRequest<{ exportId: string }>('exportReport', {
    reportId,
    body: parameters,
  });
  return exportId;
}

export async function getExportStatus(
  reportId: string,
  exportId: string
): Promise<{ status: string; percentComplete: number }> {
  return proxyRequest('getExportStatus', { reportId, exportId });
}

export async function downloadExportedPdf(
  reportId: string,
  exportId: string
): Promise<Blob> {
  const { file } = await proxyRequest<{ file: string; contentType: string }>('getExportFile', {
    reportId,
    exportId,
  });
  
  // Convert base64 to blob
  const byteCharacters = atob(file);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: 'application/pdf' });
}

// ═══════════════════════════════════════════════════════════════════════════
// Capacity Management
// ═══════════════════════════════════════════════════════════════════════════

export async function resumeCapacity(): Promise<void> {
  await proxyRequest('resumeCapacity');
}

export async function suspendCapacity(): Promise<void> {
  await proxyRequest('suspendCapacity');
}

// ═══════════════════════════════════════════════════════════════════════════
// Health Check
// ═══════════════════════════════════════════════════════════════════════════

export async function checkHealth(): Promise<{
  status: string;
  configured: { sharepoint: boolean; powerbi: boolean };
}> {
  return proxyRequest('health');
}

// ═══════════════════════════════════════════════════════════════════════════
// Export status (always configured since it uses server-side auth)
// ═══════════════════════════════════════════════════════════════════════════

export const sharePointStatus = {
  isConfigured: true, // Always true - server handles auth
  config: {
    hasClientId: true,
    hasTenantId: true,
    hasSiteId: true,
  },
};

// No longer needed - server handles list creation
export async function ensureListsExist(): Promise<void> {
  // Lists should be created manually or by Power Automate
  // This is now a no-op
  return;
}